/**
 * Everything anyone says.
 *
 * The binding rule from the tone spec: **the ball never speaks.** Not a line,
 * not a thought bubble. Golfers talk, and the other balls in the bucket talk,
 * and the player-ball answers none of them — all of its interiority is eyes and
 * body language. A ball that physically cannot speak can never break sincerity,
 * which is the whole reason the joke lands.
 *
 * The second rule: golfers may be ridiculous, the dream may not. Nothing here
 * is ever said about the cup, the flag, or the green.
 *
 * Each golfer has one voice and keeps it. Gary apologises to the ball he is
 * hitting; Rick refers to himself in the third person; Dana has been doing this
 * for forty years and would like everyone to stop talking.
 */

export type BarkSituation =
	| 'lobby'
	| 'address'
	| 'whiff'
	| 'flush'
	| 'good'
	| 'bad'
	| 'stormOff'
	| 'phone'

type Voice = {
	lobby: string[]
	/** Indexed by anger tier, 0..4. */
	address: string[][]
	whiff: string[]
	flush: string[]
	good: string[]
	bad: string[]
	stormOff: string[]
	phone: string[]
}

const GARY: Voice = {
	lobby: ['Right. Nice and easy.', 'No pressure. No pressure.', "I've been working on this."],
	address: [
		['Sorry about this.', 'Nothing personal.', 'Here we go.'],
		["That one's on me, not you.", 'New glove. That was it.', "I'm not warmed up."],
		['Okay. OKAY.', 'The sun was in my— it is cloudy.', "I'm fine. I'm fine."],
		['I have a BAD BACK.', 'THIS CLUB IS DEFECTIVE.', 'Who put you there?'],
		["I've been playing eleven years.", 'ELEVEN YEARS.', 'My wife thinks this is relaxing.'],
	],
	whiff: [
		"Where'd it go? It was right there.",
		'Did anyone see that?',
		'That was a practice one.',
		'It moved. It definitely moved.',
		'Is it under the mat?',
		'Has anyone got a torch?',
	],
	flush: ['Oh! Oh, lovely.', 'Did you see it? Did you SEE it?', "That's the one. That's the one."],
	good: ['Good enough.', "I'll take it.", 'Straight-ish.'],
	bad: ['Sorry. Sorry sorry sorry.', 'Are you alright?', "That's my fault, that one."],
	stormOff: ["I'm going to go and sit down.", 'I need a minute.', "That's enough golf."],
	phone: ["No, I'm at WORK.", "I can't— I'm literally at work.", "It's a work thing."],
}

const RICK: Voice = {
	lobby: ["Rick's feeling it today.", 'Rick was born for the range.', 'Watch this. Watch Rick.'],
	address: [
		["Rick doesn't miss.", "This is Rick's whole thing.", 'Big Rick. Big swing.'],
		["Rick's just warming up.", 'That was a read issue.', 'Rick allows one.'],
		['Rick is NOT the problem here.', 'Rick would like a different ball.', "Rick's locked in."],
		["RICK KNOWS WHAT HE'S DOING.", "Don't look at Rick.", 'RICK IS CALM.'],
		['Rick has never been angry in his life.', 'RICK. IS. FINE.', 'Rick invented this shot.'],
	],
	whiff: [
		"That doesn't count.",
		"Rick's foot slipped.",
		'Nobody writes that one down.',
		'Rick is deciding whether that happened.',
		'That was a practice swing. Obviously.',
		'The ball moved. Rick saw it move.',
	],
	flush: ['And THAT is why they call him Rick.', 'RICK!', 'Somebody measure that.'],
	good: ['Rick accepts it.', 'Fine. Fine.', "That's a Rick shot."],
	bad: ['Wind.', 'Bad lie.', "Rick's been hitting into the wind all day."],
	stormOff: ["Rick's done here.", 'Rick has somewhere to be.', 'You got lucky.'],
	phone: ["No, I'm at WORK.", "Rick's at work.", "Can't talk. Working."],
}

const DANA: Voice = {
	lobby: ["Let's get this over with.", 'Stand still.', 'Hm.'],
	address: [
		['Hm.', 'Right.', 'Yes.'],
		["Don't.", 'Again.', 'Mm.'],
		['I have played this game forty years.', 'Really.', 'Fine.'],
		['Stand. Still.', 'You are being childish.', 'I see.'],
		['...', 'I have all evening.', 'Hm.'],
	],
	whiff: [
		'Cute.',
		'Hm.',
		'Noted.',
		'Again, then.',
		'You are enjoying this.',
		'I have all evening.',
	],
	flush: ['There.', 'Yes. That.', 'Mm.'],
	good: ['Acceptable.', 'Fine.', 'Hm.'],
	bad: ['My fault.', 'Poor.', 'Again.'],
	stormOff: ['No.', 'I think not.', 'Goodbye.'],
	phone: ["No, I'm at work.", 'I am busy.', 'Later.'],
}

const VOICES: Record<string, Voice> = { gary: GARY, rick: RICK, dana: DANA }

/**
 * The other balls in the bucket. They are allowed to speak because the player
 * is not, and they never mock the dream — they confirm that it is real. World
 * building through cellmates.
 */
export const BUCKET_LINES = [
	'I saw the green once.',
	'Day four hundred in the bucket.',
	"New one. You'll learn.",
	'Just take the hit. It is easier.',
	'I was a tour ball. Ask anyone.',
	'Mind the cart path side.',
	'If you get out, tell them about us.',
	"You've got that look. The green look.",
	'Nobody comes back scuffed AND happy.',
	'That one apologises. Take him.',
	'I made it to the rough once. It was beautiful.',
	'Do not let them draw on you.',
]

/**
 * A shuffled bag per situation: every line gets said once before any line gets
 * said twice. Plain random repeats far more than people expect — nine draws
 * from twelve lines said "I saw the green once" three times — and a repeated
 * line is how a joke stops being one.
 */
const bags = new Map<string, string[]>()

function pick(key: string, lines: readonly string[], roll: number): string {
	if (lines.length === 0) return ''
	let bag = bags.get(key)
	if (!bag || bag.length === 0) {
		bag = [...lines]
		bags.set(key, bag)
	}
	// Draw without replacement, off the caller's seeded roll so barks replay.
	const i = Math.min(bag.length - 1, Math.floor(roll * bag.length))
	return bag.splice(i, 1)[0] ?? lines[0]!
}

/**
 * What this golfer says right now. `roll` is a 0..1 from the caller's seeded
 * stream, so barks stay part of a replay rather than drifting off it.
 */
export function bark(
	golferId: string,
	situation: BarkSituation,
	anger: number,
	roll: number,
): string {
	const voice = VOICES[golferId] ?? GARY
	if (situation === 'address') {
		const tier = voice.address[Math.min(anger, voice.address.length - 1)] ?? voice.address[0]!
		return pick(`${golferId}:address`, tier, roll)
	}
	return pick(`${golferId}:${situation}`, voice[situation], roll)
}

export function bucketLine(roll: number): string {
	return pick('bucket', BUCKET_LINES, roll)
}
